import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomRadarChart } from '../components/RadarChart';
import { responses } from '../services/api';
import CourseCompletionGrid from '../components/CourseCompletionGrid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface RadarDataPoint {
  category: string;
  value: number;
}

const GrowthStrengthAreas: React.FC = () => {
  const [strengthArea, setStrengthArea] = useState<RadarDataPoint | null>(null);
  const [growthArea, setGrowthArea] = useState<RadarDataPoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRadarData = async () => {
      try {
        const response = await responses.getRadarChartData();
        const latestData: RadarDataPoint[] = response.data.latest;
        if (latestData && latestData.length > 0) {
          let highest: RadarDataPoint = latestData[0];
          let lowest: RadarDataPoint = latestData[0];
          latestData.forEach((item: RadarDataPoint) => {
            if (item.value > highest.value) highest = item;
            if (item.value < lowest.value) lowest = item;
          });
          setStrengthArea(highest);
          setGrowthArea(lowest);
        }
      } catch (err) {
        setError('Failed to load growth and strength areas');
      } finally {
        setLoading(false);
      }
    };
    fetchRadarData();
  }, []);

  if (loading) {
    return <Typography variant="body2" color="text.secondary">Loading growth and strength areas...</Typography>;
  }
  if (error) {
    return <Typography variant="body2" color="error">{error}</Typography>;
  }
  return (
    <Box mt={2}>
      <Typography variant="subtitle1" fontWeight="bold">Growth Area</Typography>
      <Typography variant="body2" color="text.secondary">
        {growthArea ? `${growthArea.category} (score: ${growthArea.value})` : 'No growth area identified.'}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle1" fontWeight="bold">Strength Area</Typography>
      <Typography variant="body2" color="text.secondary">
        {strengthArea ? `${strengthArea.category} (score: ${strengthArea.value})` : 'No strength area identified.'}
      </Typography>
    </Box>
  );
};

const TrainingProgress = () => {
  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>Course Progress</Typography>
      
      <Box
        sx={{
          backgroundColor: 'transparent', // Keep transparent to match current dark theme
          width: '100%',
          maxWidth: 400,
          mx: 'auto',
          overflow: 'hidden'
        }}
      >
        {/* Graph Area */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 160, px: 4, mt: 3 }}>
          {/* Life Plan Oval */}
          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 40,
                height: 160,
                backgroundColor: '#1de9b6',
                borderRadius: 20,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center'
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', mt: 1 }} />
            </Box>
          </Box>

          {/* Foundations Oval */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
            <Box
              sx={{
                width: 40,
                height: 30,
                backgroundColor: '#1de9b6',
                borderRadius: 15,
                position: 'relative'
              }}
            />
            <LocationOnIcon color="primary" sx={{ position: 'absolute', bottom: 30, fontSize: 20 }} />
          </Box>

          {/* Supporting Others Oval */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
            <Box
              sx={{
                width: 40,
                height: 30,
                backgroundColor: '#cfd8dc',
                borderRadius: 15
              }}
            />
          </Box>

          {/* Dotted Line */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '10%',
              width: '80%',
              borderTop: '2px dotted #888' // Darker dotted line for visibility on dark background
            }}
          />
        </Box>

        {/* Captions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', py: 2, px: 2 }}>
          <Typography variant="caption" align="center" sx={{ color: 'white' }}>
            Life Plan
            <br />
            (Roadmap)
          </Typography>
          <Typography variant="caption" align="center" sx={{ color: '#3f51b5' }}>
            Foundations in
            <br />
            Whole Health
          </Typography>
          <Typography variant="caption" align="center" sx={{ color: '#cfd8dc' }}>
            Supporting
            <br />
            Others
          </Typography>
        </Box>
      </Box>
      

    </Box>
  );
};

const SectionBox = ({ title }: { title: string }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      minHeight: 160,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: 4,
      justifyContent: 'space-between',
      height: '100%',
    }}
  >
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{
          fontSize: { xs: '1rem', md: '1.05rem' },
          textAlign: 'center',
          mb: 1,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontSize: '0.92rem',
          textAlign: 'center',
          mt: 1,
        }}
      >
        {/* Placeholder summary text, ~200 chars */}
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque euismod, urna eu tincidunt consectetur, nisi nisl aliquam nunc, vitae euismod nisl nunc eget lorem. Proin non felis vitae augue dictum.
      </Typography>
    </Box>
  </Paper>
);

// Define the default callback question ID as a constant
const DEFAULT_CALLBACK_QUESTION_ID = 13945;

// Styled components for the header
const HeaderLayout = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2), // Reduced margin bottom
}));

const LogoContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  backgroundColor: '#3f51b5', // Blue background color
  borderRadius: '12px',
  padding: theme.spacing(1, 3), // Reduced vertical padding to make it shorter
  display: 'flex',
  alignItems: 'center',
  gap: '8px', // Reduced gap between logo and text
  color: 'white',
  width: 'fit-content',
  minWidth: '400px', // Minimum width to make it wider
  maxWidth: '550px', // Increased max width
}));

const StyledLogo = styled('img')({
  height: '80px', // Adjusted to fit the shorter box
  width: 'auto',
  objectFit: 'contain',
});

const InfoContainer = styled(Box)({
  display: 'flex',
  gap: '16px',
});

const InfoBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  backgroundColor: 'white',
  color: 'black',
  borderRadius: '8px',
  padding: theme.spacing(1.5, 2),
  minWidth: '120px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  border: '1px solid #e0e0e0',
}));

const CourseCompletion: React.FC = () => {
  const [callbackQuestionId] = useState<number>(DEFAULT_CALLBACK_QUESTION_ID);
  const [error, setError] = useState<string | null>(null);
  const [courseName, setCourseName] = useState<string>('Life Plan');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get current date in the format: April 19, 2025
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  useEffect(() => {
    // Get course information from state if available
    const state = location.state as { courseId?: number; courseName?: string } | null;
    
    if (state?.courseName) {
      setCourseName(state.courseName);
    }
  }, [location]);
  

  return (
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 1300, mx: 'auto' }}>
      {error && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      
      {/* Header layout with separate blue box and info boxes */}
      <HeaderLayout>
        {/* Blue box with logo and title only */}
        <LogoContainer>
          <StyledLogo src="/assets/logo.png" alt="A Wise Friend" />
          <Typography variant="h5" fontWeight="bold">{courseName} Snapshot</Typography>
        </LogoContainer>
        
        {/* Separate date and version boxes on the right */}
        <InfoContainer>
          <InfoBox>
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>Date</Typography>
            <Typography variant="body1" fontWeight="medium">{currentDate}</Typography>
          </InfoBox>
          <InfoBox>
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>Version</Typography>
            <Typography variant="body1" fontWeight="medium">v1</Typography>
          </InfoBox>
        </InfoContainer>
      </HeaderLayout>
      
      <CourseCompletionGrid callbackQuestionId={callbackQuestionId} />
      
      {/* Progress Overview 70%, Course Progress 30% */}
      <Grid container spacing={3} alignItems="stretch" sx={{ mt: 4 }}>
        <Grid item xs={12} md={8.4 /* 70% of 12 */}>
          <Paper elevation={2} sx={{ p: 2, minHeight: 400, height: '100%', borderRadius: 4, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Progress Overview</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', height: 300, gap: 3, width: '100%' }}>
              <Box sx={{ width: { xs: 480, md: 576 }, height: 336, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                <CustomRadarChart />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'stretch' }}>
                <GrowthStrengthAreas />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3.6 /* 30% of 12 */}>
          <Paper elevation={2} sx={{ p: 2, minHeight: 400, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', borderRadius: 4 }}>
            <TrainingProgress />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CourseCompletion;
